import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { API, SHOP_URL } from './api'
import { adminRequest } from './EshopCategories'
import { useDragSort } from './dragSort'
import type { Background, SiteDesign, SiteSection } from './siteTypes'

/* ------------------------------------------------------------------ */
/*  Konštanty                                                          */
/* ------------------------------------------------------------------ */

const HEADING_FONTS = ['Playfair Display', 'Cormorant Garamond', 'DM Serif Display', 'Lora', 'Fraunces', 'Prata', 'DM Sans', 'Poppins', 'Montserrat']
const BODY_FONTS = ['DM Sans', 'Inter', 'Nunito', 'Lato', 'Poppins', 'Montserrat', 'Source Sans 3', 'Lora']

const PALETTES: Array<{ name: string; theme: Pick<SiteDesign['theme'], 'primary' | 'background' | 'surface' | 'text' | 'soft'> }> = [
  { name: 'Emili (pôvodná)', theme: { primary: '#c95e5b', background: '#fbf7f1', surface: '#fffdf9', text: '#382820', soft: '#f0e4dc' } },
  { name: 'Malinová', theme: { primary: '#d6336c', background: '#fff8fa', surface: '#ffffff', text: '#3a1d2a', soft: '#fbe3ec' } },
  { name: 'Čokoládová', theme: { primary: '#7b4a2e', background: '#f8f2ec', surface: '#fffcf8', text: '#2f1e15', soft: '#ead9cb' } },
  { name: 'Pistáciová', theme: { primary: '#5f8a4c', background: '#f7f9f1', surface: '#ffffff', text: '#24301f', soft: '#e3ecd6' } },
  { name: 'Levanduľová', theme: { primary: '#7b5ea7', background: '#f9f7fc', surface: '#ffffff', text: '#2b2238', soft: '#ebe3f5' } },
  { name: 'Vianočná', theme: { primary: '#b3262e', background: '#fbf8f3', surface: '#ffffff', text: '#1f2b24', soft: '#e7efe7' } },
  { name: 'Elegantná tmavá', theme: { primary: '#c9a35a', background: '#f6f3ee', surface: '#ffffff', text: '#1c1b1a', soft: '#ebe4d6' } },
]

const EMOJIS = ['🎂', '🍰', '🧁', '🍫', '🍬', '🍭', '🍩', '🍪', '🥐', '🍓', '🍒', '🍋', '🫐', '🍑', '🥥', '☕', '🍵', '🥛', '🎁', '💝', '❤️', '♡', '✨', '🌸', '🌷', '🌿', '🎄', '⭐', '🎉', '🥳', '🐣', '🐰', '🎃', '❄️', '☀️', '🏠', '🚚', '💳', '📞', '📍', '⏰', '✅', '👩‍🍳', '👨‍🍳', '🥇', '💯', '😋', '🤤', '👌', '🙏']

const BLOCKS: Array<{ type: SiteSection['type']; label: string; icon: string; hint: string }> = [
  { type: 'textImage', label: 'Text s obrázkom', icon: '▤', hint: 'Príbeh, novinka, ponuka' },
  { type: 'banner', label: 'Veľký banner', icon: '▭', hint: 'Fotka cez celú šírku s textom' },
  { type: 'cta', label: 'Výzva s tlačidlom', icon: '➜', hint: 'Napr. „Torty na objednávku“' },
  { type: 'quote', label: 'Citát / recenzia', icon: '❝', hint: 'Slová zákazníka' },
  { type: 'gallery', label: 'Galéria', icon: '▦', hint: 'Fotky výrobkov a prevádzok' },
  { type: 'benefits', label: 'Výhody s ikonami', icon: '✓', hint: 'Krátke body s emotikonmi' },
]

const LABELS: Record<SiteSection['type'], string> = {
  hero: 'Úvod (hlavný banner)', benefits: 'Výhody', products: 'Produkty', about: 'O nás a prevádzky', textImage: 'Text s obrázkom',
  banner: 'Banner', quote: 'Citát', gallery: 'Galéria', cta: 'Výzva', seasonal: 'Vianočná objednávka',
}
const ICONS: Record<SiteSection['type'], string> = { hero: '★', benefits: '✓', products: '🛍', about: '♡', textImage: '▤', banner: '▭', quote: '❝', gallery: '▦', cta: '➜', seasonal: '🎄' }

const newId = (type: string) => `${type.toLowerCase().replace(/[^a-z]/g, '')}-${Math.random().toString(36).slice(2, 7)}`

function newSection(type: SiteSection['type']): SiteSection {
  const id = newId(type)
  switch (type) {
    case 'textImage': return { id, type, enabled: true, eyebrow: 'Novinka', title: 'Nadpis bloku', text: 'Sem napíšte text – napríklad o novej torte, sezónnej ponuke alebo o tom, ako pečiete.', imageUrl: '', imageSide: 'left', buttonText: '', buttonUrl: '#produkty', background: 'none' }
    case 'banner': return { id, type, enabled: true, title: 'Torty na každú oslavu', text: 'Narodeniny, svadby, krstiny – pripravíme tortu podľa vašich predstáv.', imageUrl: '', buttonText: 'Pozrieť torty', buttonUrl: '#produkty', height: 'medium', align: 'left' }
    case 'cta': return { id, type, enabled: true, emoji: '🎂', title: 'Tortu na objednávku vám radi pripravíme', text: 'Zavolajte nám a dohodneme sa na detailoch.', buttonText: 'Zavolať', buttonUrl: 'tel:+421903549448', background: 'soft' }
    case 'quote': return { id, type, enabled: true, emoji: '💝', text: 'Najlepšia torta, akú sme kedy mali. Ďakujeme!', author: 'Jana z Prievidze', background: 'none' }
    case 'gallery': return { id, type, enabled: true, title: 'Z našej cukrárne', images: [] }
    case 'benefits': return { id, type, enabled: true, items: [{ emoji: '🍰', title: 'Čerstvé každý deň', text: 'Pečieme z poctivých surovín' }, { emoji: '🎁', title: 'Darčekové balenie', text: 'Na požiadanie zabalíme' }] }
    case 'hero': return { id, type, enabled: true, eyebrow: '', title: 'Nadpis', titleAccent: '', text: '', buttonText: 'Pozrieť produkty', buttonUrl: '#produkty', imageUrl: '', note: '', layout: 'imageRight' }
    case 'about': return { id, type, enabled: true, eyebrow: '', title: 'O nás', titleAccent: '', text: '', emoji: '♡', showStores: true, storesTitle: 'Naše prevádzky' }
    case 'products': return { id, type, enabled: true, eyebrow: '', title: 'Produkty', text: '' }
    case 'seasonal': return { id: 'seasonal', type, enabled: true, kicker: '', title: '', text: '', buttonText: '', showFacts: true, showPhotos: true, style: 'green', images: [] }
  }
}

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value))

/* ------------------------------------------------------------------ */
/*  Malé ovládacie prvky                                              */
/* ------------------------------------------------------------------ */

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="se-field"><span className="se-label">{label}</span>{children}{hint && <small className="se-hint">{hint}</small>}</label>
}

function TextInput({ value, onChange, max, placeholder, multiline, rows = 3 }: { value: string; onChange: (v: string) => void; max: number; placeholder?: string; multiline?: boolean; rows?: number }) {
  return <span className="se-text">
    {multiline
      ? <textarea value={value} maxLength={max} rows={rows} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
      : <input value={value} maxLength={max} placeholder={placeholder} onChange={e => onChange(e.target.value)} />}
    {value.length > max * 0.8 && <small className="se-count">{value.length}/{max}</small>}
  </span>
}

function Segmented<T extends string>({ value, options, onChange }: { value: T; options: Array<[T, string]>; onChange: (v: T) => void }) {
  return <div className="se-seg" role="radiogroup">{options.map(([v, label]) => <button key={v} type="button" role="radio" aria-checked={value === v} className={value === v ? 'is-on' : ''} onClick={() => onChange(v)}>{label}</button>)}</div>
}

function Toggle({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <label className={`se-toggle ${disabled ? 'is-disabled' : ''}`}><button type="button" role="switch" aria-checked={checked} disabled={disabled} className={`pa-switch ${checked ? 'is-on' : ''}`} onClick={() => onChange(!checked)}><span /></button><span>{label}</span></label>
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return <div className="se-color">
    <input type="color" value={value} onChange={e => onChange(e.target.value)} aria-label={label} />
    <span>{label}</span>
    <input className="se-hex" value={draft} maxLength={7} onChange={e => { setDraft(e.target.value); if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) onChange(e.target.value.toLowerCase()) }} />
  </div>
}

function EmojiField({ value, onChange, label = 'Emotikon' }: { value: string; onChange: (v: string) => void; label?: string }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])
  return <div className="se-field se-emoji" ref={box}>
    <span className="se-label">{label}</span>
    <div className="se-emoji-row">
      <button type="button" className="se-emoji-btn" onClick={() => setOpen(o => !o)} aria-label="Vybrať emotikon">{value || '＋'}</button>
      <input value={value} maxLength={16} onChange={e => onChange(e.target.value)} placeholder="alebo napíšte" />
      {value && <button type="button" className="se-mini" onClick={() => onChange('')} aria-label="Odstrániť emotikon">×</button>}
    </div>
    {open && <div className="se-emoji-pop" role="listbox">{EMOJIS.map(e => <button key={e} type="button" onClick={() => { onChange(e); setOpen(false) }}>{e}</button>)}</div>}
  </div>
}

const assetUrl = (url: string) => url.startsWith('/uploads/') ? `${API}${url}` : url.startsWith('/images/') ? `${SHOP_URL}${url}` : url

async function uploadImage(file: File): Promise<string> {
  const form = new FormData()
  form.append('image', file)
  const response = await fetch(`${API}/api/admin/site/images`, { method: 'POST', body: form, credentials: 'include' })
  const data = await response.json().catch(() => null)
  if (!response.ok || !data?.url) throw new Error(data?.error || 'Obrázok sa nepodarilo nahrať (JPG, PNG alebo WebP do 6 MB).')
  return data.url
}

function ImageField({ label, value, onChange, hint }: { label: string; value: string; onChange: (v: string) => void; hint?: string }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [over, setOver] = useState(false)
  const pick = async (file?: File | null) => {
    if (!file) return
    setBusy(true); setError('')
    try { onChange(await uploadImage(file)) } catch (e) { setError(e instanceof Error ? e.message : 'Nahratie zlyhalo.') } finally { setBusy(false) }
  }
  return <div className="se-field">
    <span className="se-label">{label}</span>
    <label className={`se-image ${over ? 'is-over' : ''} ${value ? 'has-image' : ''}`}
      onDragOver={e => { e.preventDefault(); setOver(true) }} onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); void pick(e.dataTransfer.files?.[0]) }}>
      {value ? <img src={assetUrl(value)} alt="" /> : <span className="se-image-empty">{busy ? 'Nahrávam…' : <>Pretiahnite sem fotku<br /><small>alebo kliknite – JPG, PNG, WebP</small></>}</span>}
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={e => { void pick(e.target.files?.[0]); e.target.value = '' }} />
    </label>
    {value && <div className="se-image-actions"><span>{busy ? 'Nahrávam…' : 'Kliknite na obrázok pre výmenu'}</span><button type="button" className="se-mini-link" onClick={() => onChange('')}>Odstrániť</button></div>}
    {hint && <small className="se-hint">{hint}</small>}
    {error && <small className="se-error">{error}</small>}
  </div>
}

function LinkField({ value, onChange, label = 'Odkaz tlačidla' }: { value: string; onChange: (v: string) => void; label?: string }) {
  const presets: Array<[string, string]> = [['#produkty', 'Produkty'], ['#onas', 'O nás'], ['#kontakt', 'Kontakt']]
  return <Field label={label} hint="Sekcia na stránke, https://… , tel:… alebo mailto:…">
    <div className="se-link">
      <input value={value} maxLength={500} placeholder="#produkty" onChange={e => onChange(e.target.value.trim())} />
      <div className="se-link-presets">{presets.map(([v, l]) => <button key={v} type="button" className={value === v ? 'is-on' : ''} onClick={() => onChange(v)}>{l}</button>)}</div>
    </div>
  </Field>
}

const BG_OPTIONS: Array<[Background, string]> = [['none', 'Bez'], ['soft', 'Jemné'], ['primary', 'Farebné'], ['dark', 'Tmavé']]

/* ------------------------------------------------------------------ */
/*  Editor jednej sekcie                                               */
/* ------------------------------------------------------------------ */

function SectionEditor({ section, onChange }: { section: SiteSection; onChange: (s: SiteSection) => void }) {
  const set = <K extends string>(key: K, value: unknown) => onChange({ ...section, [key]: value } as SiteSection)
  switch (section.type) {
    case 'seasonal': return <>
      <p className="se-info">Blok sa zákazníkom zobrazí iba počas zapnutej sezóny. Dátumy, výrobky a limity nastavíte v <b>Objednávky → 🎄 Vianoce</b>. Prázdne polia = texty zo sezóny.</p>
      <Field label="Farba"><Segmented value={section.style} options={[['green', '🌲 Zelená'], ['red', '🍒 Červená'], ['gold', '✨ Zlatá'], ['dark', '🌙 Tmavá']]} onChange={v => set('style', v)} /></Field>
      <Field label="Malý nadpis" hint="Prázdne = názov sezóny (napr. Vianoce 2026)"><TextInput value={section.kicker} max={60} onChange={v => set('kicker', v)} /></Field>
      <Field label="Nadpis" hint="Prázdne = Vianočná objednávka"><TextInput value={section.title} max={100} onChange={v => set('title', v)} /></Field>
      <Field label="Text" hint="Prázdne = text zo sezóny"><TextInput value={section.text} max={400} multiline onChange={v => set('text', v)} /></Field>
      <Field label="Text tlačidla" hint="Prázdne = Otvoriť vianočnú ponuku"><TextInput value={section.buttonText} max={60} onChange={v => set('buttonText', v)} /></Field>
      <Toggle checked={section.showFacts} onChange={v => set('showFacts', v)} label="Zobraziť dátumy (objednávky do, vyzdvihnutie, platba)" />
      <Field label="Vlastné fotky" hint="Nahrajte 1 fotku (zobrazí sa cez celý blok) alebo 2–3 fotky (poskladajú sa vedľa seba). Bez vlastnej fotky sa – ak je to zapnuté nižšie – použijú fotky vianočných výrobkov.">
        <div className="se-gallery">
          {section.images.map((url, i) => <div key={url + i} className="se-gallery-item">
            <img src={assetUrl(url)} alt="" />
            <span>
              <button type="button" className="se-mini" disabled={i === 0} onClick={() => { const next = [...section.images];[next[i - 1], next[i]] = [next[i], next[i - 1]]; set('images', next) }} aria-label="Dopredu">←</button>
              <button type="button" className="se-mini" disabled={i === section.images.length - 1} onClick={() => { const next = [...section.images];[next[i + 1], next[i]] = [next[i], next[i + 1]]; set('images', next) }} aria-label="Dozadu">→</button>
              <button type="button" className="se-mini" onClick={() => set('images', section.images.filter((_, j) => j !== i))} aria-label="Odstrániť">×</button>
            </span>
          </div>)}
        </div>
        {section.images.length < 3 && <ImageField label="Pridať fotku" value="" onChange={v => v && set('images', [...section.images, v])} hint={`Najviac 3 fotky (teraz ${section.images.length}).`} />}
      </Field>
      <Toggle checked={section.showPhotos} onChange={v => set('showPhotos', v)} label="Bez vlastných fotiek zobraziť fotky vianočných výrobkov" disabled={section.images.length > 0} />
    </>
    case 'hero': return <>
      <Field label="Rozloženie"><Segmented value={section.layout} options={[['imageRight', 'Fotka vpravo'], ['imageLeft', 'Fotka vľavo'], ['centered', 'Iba text']]} onChange={v => set('layout', v)} /></Field>
      <Field label="Malý nadpis nad titulkom"><TextInput value={section.eyebrow} max={80} onChange={v => set('eyebrow', v)} /></Field>
      <Field label="Titulok"><TextInput value={section.title} max={80} onChange={v => set('title', v)} /></Field>
      <Field label="Druhý riadok titulku (farebný)"><TextInput value={section.titleAccent} max={80} onChange={v => set('titleAccent', v)} /></Field>
      <Field label="Text"><TextInput value={section.text} max={400} multiline rows={4} onChange={v => set('text', v)} /></Field>
      <Field label="Text tlačidla" hint="Prázdne = bez tlačidla"><TextInput value={section.buttonText} max={40} onChange={v => set('buttonText', v)} /></Field>
      {section.buttonText && <LinkField value={section.buttonUrl} onChange={v => set('buttonUrl', v)} />}
      {section.layout !== 'centered' && <ImageField label="Fotka" value={section.imageUrl} onChange={v => set('imageUrl', v)} hint="Ideálne na výšku alebo štvorcová, aspoň 1200 px." />}
      <Field label="Poznámka pod tlačidlom"><TextInput value={section.note} max={120} onChange={v => set('note', v)} /></Field>
    </>
    case 'benefits': {
      const items = section.items
      const setItem = (i: number, patch: Partial<typeof items[number]>) => set('items', items.map((it, j) => j === i ? { ...it, ...patch } : it))
      return <>
        {items.map((item, i) => <div className="se-item" key={i}>
          <div className="se-item-head"><strong>Bod {i + 1}</strong><span>
            <button type="button" className="se-mini" disabled={i === 0} onClick={() => { const next = [...items];[next[i - 1], next[i]] = [next[i], next[i - 1]]; set('items', next) }} aria-label="Vyššie">↑</button>
            <button type="button" className="se-mini" disabled={i === items.length - 1} onClick={() => { const next = [...items];[next[i + 1], next[i]] = [next[i], next[i + 1]]; set('items', next) }} aria-label="Nižšie">↓</button>
            <button type="button" className="se-mini" disabled={items.length <= 1} onClick={() => set('items', items.filter((_, j) => j !== i))} aria-label="Odstrániť">×</button>
          </span></div>
          <EmojiField value={item.emoji} onChange={v => setItem(i, { emoji: v })} label="Ikona" />
          <Field label="Nadpis"><TextInput value={item.title} max={60} onChange={v => setItem(i, { title: v })} /></Field>
          <Field label="Popis"><TextInput value={item.text} max={120} onChange={v => setItem(i, { text: v })} /></Field>
        </div>)}
        {items.length < 6 && <button type="button" className="se-add" onClick={() => set('items', [...items, { emoji: '✨', title: 'Nový bod', text: '' }])}>＋ Pridať bod</button>}
      </>
    }
    case 'products': return <>
      <p className="se-info">Produkty a kategórie sa berú z časti Produkty a Kategórie. Tu meníte iba nadpis sekcie. Sekciu môžete vypnúť prepínačom vyššie – napríklad keď chcete dočasne spustiť iba vianočné objednávky.</p>
      <Field label="Malý nadpis"><TextInput value={section.eyebrow} max={80} onChange={v => set('eyebrow', v)} /></Field>
      <Field label="Nadpis"><TextInput value={section.title} max={80} onChange={v => set('title', v)} /></Field>
      <Field label="Text vpravo"><TextInput value={section.text} max={200} multiline onChange={v => set('text', v)} /></Field>
    </>
    case 'about': return <>
      <EmojiField value={section.emoji} onChange={v => set('emoji', v)} label="Symbol v kruhu" />
      <Field label="Malý nadpis"><TextInput value={section.eyebrow} max={80} onChange={v => set('eyebrow', v)} /></Field>
      <Field label="Nadpis"><TextInput value={section.title} max={80} onChange={v => set('title', v)} /></Field>
      <Field label="Druhý riadok nadpisu (farebný)"><TextInput value={section.titleAccent} max={80} onChange={v => set('titleAccent', v)} /></Field>
      <Field label="Text"><TextInput value={section.text} max={800} multiline rows={5} onChange={v => set('text', v)} /></Field>
      <Toggle checked={section.showStores} onChange={v => set('showStores', v)} label="Zobraziť zoznam prevádzok" />
      {section.showStores && <Field label="Nadpis zoznamu prevádzok"><TextInput value={section.storesTitle} max={60} onChange={v => set('storesTitle', v)} /></Field>}
    </>
    case 'textImage': return <>
      <ImageField label="Obrázok" value={section.imageUrl} onChange={v => set('imageUrl', v)} />
      {section.imageUrl && <Field label="Obrázok na strane"><Segmented value={section.imageSide} options={[['left', 'Vľavo'], ['right', 'Vpravo']]} onChange={v => set('imageSide', v)} /></Field>}
      <Field label="Malý nadpis"><TextInput value={section.eyebrow} max={80} onChange={v => set('eyebrow', v)} /></Field>
      <Field label="Nadpis"><TextInput value={section.title} max={100} onChange={v => set('title', v)} /></Field>
      <Field label="Text" hint="Nový riadok = Enter"><TextInput value={section.text} max={1500} multiline rows={6} onChange={v => set('text', v)} /></Field>
      <Field label="Text tlačidla" hint="Prázdne = bez tlačidla"><TextInput value={section.buttonText} max={40} onChange={v => set('buttonText', v)} /></Field>
      {section.buttonText && <LinkField value={section.buttonUrl} onChange={v => set('buttonUrl', v)} />}
      <Field label="Pozadie"><Segmented value={section.background} options={BG_OPTIONS} onChange={v => set('background', v)} /></Field>
    </>
    case 'banner': return <>
      <ImageField label="Fotka na pozadí" value={section.imageUrl} onChange={v => set('imageUrl', v)} hint="Široká fotka, aspoň 1600 px. Text sa automaticky stmaví, aby bol čitateľný." />
      <Field label="Nadpis"><TextInput value={section.title} max={100} onChange={v => set('title', v)} /></Field>
      <Field label="Text"><TextInput value={section.text} max={300} multiline onChange={v => set('text', v)} /></Field>
      <Field label="Text tlačidla"><TextInput value={section.buttonText} max={40} onChange={v => set('buttonText', v)} /></Field>
      {section.buttonText && <LinkField value={section.buttonUrl} onChange={v => set('buttonUrl', v)} />}
      <Field label="Výška"><Segmented value={section.height} options={[['small', 'Nízky'], ['medium', 'Stredný'], ['large', 'Vysoký']]} onChange={v => set('height', v)} /></Field>
      <Field label="Zarovnanie textu"><Segmented value={section.align} options={[['left', 'Vľavo'], ['center', 'Na stred']]} onChange={v => set('align', v)} /></Field>
    </>
    case 'quote': return <>
      <EmojiField value={section.emoji} onChange={v => set('emoji', v)} />
      <Field label="Citát"><TextInput value={section.text} max={500} multiline rows={4} onChange={v => set('text', v)} /></Field>
      <Field label="Autor"><TextInput value={section.author} max={80} onChange={v => set('author', v)} /></Field>
      <Field label="Pozadie"><Segmented value={section.background} options={BG_OPTIONS} onChange={v => set('background', v)} /></Field>
    </>
    case 'gallery': return <>
      <Field label="Nadpis"><TextInput value={section.title} max={100} onChange={v => set('title', v)} /></Field>
      <div className="se-gallery">
        {section.images.map((url, i) => <div key={url + i} className="se-gallery-item">
          <img src={assetUrl(url)} alt="" />
          <span>
            <button type="button" className="se-mini" disabled={i === 0} onClick={() => { const next = [...section.images];[next[i - 1], next[i]] = [next[i], next[i - 1]]; set('images', next) }} aria-label="Dopredu">←</button>
            <button type="button" className="se-mini" disabled={i === section.images.length - 1} onClick={() => { const next = [...section.images];[next[i + 1], next[i]] = [next[i], next[i + 1]]; set('images', next) }} aria-label="Dozadu">→</button>
            <button type="button" className="se-mini" onClick={() => set('images', section.images.filter((_, j) => j !== i))} aria-label="Odstrániť">×</button>
          </span>
        </div>)}
      </div>
      {section.images.length < 12 && <ImageField label="Pridať fotku" value="" onChange={v => v && set('images', [...section.images, v])} hint={`Najviac 12 fotiek (teraz ${section.images.length}).`} />}
    </>
    case 'cta': return <>
      <EmojiField value={section.emoji} onChange={v => set('emoji', v)} />
      <Field label="Nadpis"><TextInput value={section.title} max={100} onChange={v => set('title', v)} /></Field>
      <Field label="Text"><TextInput value={section.text} max={300} multiline onChange={v => set('text', v)} /></Field>
      <Field label="Text tlačidla"><TextInput value={section.buttonText} max={40} onChange={v => set('buttonText', v)} /></Field>
      {section.buttonText && <LinkField value={section.buttonUrl} onChange={v => set('buttonUrl', v)} />}
      <Field label="Pozadie"><Segmented value={section.background} options={BG_OPTIONS} onChange={v => set('background', v)} /></Field>
    </>
  }
}

/* ------------------------------------------------------------------ */
/*  Hlavný editor                                                      */
/* ------------------------------------------------------------------ */

type Tab = 'sections' | 'style' | 'header' | 'footer' | 'company'
type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

export default function SiteEditor() {
  const [design, setDesign] = useState<SiteDesign | null>(null)
  const [published, setPublished] = useState<SiteDesign | null>(null)
  const [defaults, setDefaults] = useState<SiteDesign | null>(null)
  const [tab, setTab] = useState<Tab>('sections')
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop')
  const [save, setSave] = useState<SaveState>('idle')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [publishing, setPublishing] = useState(false)
  const [frameReady, setFrameReady] = useState(false)
  const history = useRef<{ past: SiteDesign[]; future: SiteDesign[]; lastPush: number }>({ past: [], future: [], lastPush: 0 })
  const [, forceRender] = useState(0)
  const frame = useRef<HTMLIFrameElement>(null)
  const saveTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    adminRequest('/api/admin/site').then(data => {
      setDesign(data.draft); setPublished(data.published); setDefaults(data.defaults)
    }).catch(e => setError(e instanceof Error ? e.message : 'Vzhľad sa nepodarilo načítať.'))
  }, [])

  // Odoslanie návrhu do náhľadu e-shopu.
  const postToFrame = useCallback((message: object) => { frame.current?.contentWindow?.postMessage(message, SHOP_URL) }, [])
  useEffect(() => { if (design && frameReady) postToFrame({ type: 'emili-site-preview', design }) }, [design, frameReady, postToFrame])
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== new URL(SHOP_URL).origin || !event.data || typeof event.data !== 'object') return
      if (event.data.type === 'emili-site-ready') setFrameReady(true)
      if (event.data.type === 'emili-site-select' && typeof event.data.sectionId === 'string') { setTab('sections'); setAdding(false); setEditing(event.data.sectionId) }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])
  useEffect(() => { if (frameReady) postToFrame({ type: 'emili-site-focus', sectionId: tab === 'sections' ? editing : null }) }, [editing, tab, frameReady, postToFrame])

  // Automatické ukladanie konceptu.
  const scheduleSave = useCallback((next: SiteDesign) => {
    setSave('dirty')
    window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(async () => {
      setSave('saving')
      try {
        await adminRequest('/api/admin/site/draft', { method: 'PUT', body: JSON.stringify({ design: next }) })
        setSave('saved'); setError('')
      } catch (e) { setSave('error'); setError(e instanceof Error ? e.message : 'Koncept sa nepodarilo uložiť.') }
    }, 900)
  }, [])

  const update = useCallback((mutate: (d: SiteDesign) => SiteDesign) => {
    setDesign(current => {
      if (!current) return current
      const next = mutate(current)
      const h = history.current
      const now = Date.now()
      if (now - h.lastPush > 700) { h.past.push(current); if (h.past.length > 80) h.past.shift() }
      h.lastPush = now; h.future = []
      scheduleSave(next)
      return next
    })
  }, [scheduleSave])

  const undo = useCallback(() => {
    const h = history.current
    setDesign(current => {
      if (!current || !h.past.length) return current
      const prev = h.past.pop()!
      h.future.push(current); h.lastPush = 0
      scheduleSave(prev)
      return prev
    })
    forceRender(n => n + 1)
  }, [scheduleSave])
  const redo = useCallback(() => {
    const h = history.current
    setDesign(current => {
      if (!current || !h.future.length) return current
      const next = h.future.pop()!
      h.past.push(current); h.lastPush = 0
      scheduleSave(next)
      return next
    })
    forceRender(n => n + 1)
  }, [scheduleSave])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return
      const target = e.target as HTMLElement
      if (target.closest('input,textarea')) return
      e.preventDefault(); if (e.shiftKey) redo(); else undo()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo])

  const sectionIds = useMemo(() => design?.sections.map(s => s.id) ?? [], [design])
  const sectionDrag = useDragSort('site-sections', sectionIds, ids => update(d => ({ ...d, sections: ids.map(id => d.sections.find(s => s.id === id)!).filter(Boolean) })))

  async function publish() {
    if (!design) return
    setPublishing(true); setError(''); setNotice('')
    try {
      window.clearTimeout(saveTimer.current)
      await adminRequest('/api/admin/site/publish', { method: 'POST', body: JSON.stringify({ design }) })
      setPublished(clone(design)); setSave('saved')
      setNotice('Zverejnené – zákazníci už vidia nový vzhľad.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Zverejnenie sa nepodarilo.') }
    finally { setPublishing(false) }
  }
  async function discard() {
    if (!window.confirm('Zahodiť všetky nezverejnené zmeny a vrátiť sa k verzii, ktorú vidia zákazníci?')) return
    window.clearTimeout(saveTimer.current)
    try {
      const data = await adminRequest('/api/admin/site/discard', { method: 'POST' })
      history.current = { past: design ? [design] : [], future: [], lastPush: 0 }
      setDesign(data.draft); setSave('saved'); setEditing(null)
      setNotice('Zmeny sú zahodené.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Nepodarilo sa.') }
  }

  if (!design) return <div className="panel"><p className="empty-message">{error || 'Načítavam editor…'}</p></div>

  const unpublished = published ? JSON.stringify(design) !== JSON.stringify(published) : false
  const editingSection = design.sections.find(s => s.id === editing) ?? null
  const setSection = (s: SiteSection) => update(d => ({ ...d, sections: d.sections.map(x => x.id === s.id ? s : x) }))
  const theme = design.theme
  const setTheme = (patch: Partial<SiteDesign['theme']>) => update(d => ({ ...d, theme: { ...d.theme, ...patch } }))
  const brand = design.brand
  const setBrand = (patch: Partial<SiteDesign['brand']>) => update(d => ({ ...d, brand: { ...d.brand, ...patch } }))
  const foot = design.footer
  const setFoot = (patch: Partial<SiteDesign['footer']>) => update(d => ({ ...d, footer: { ...d.footer, ...patch } }))

  const addBlock = (type: SiteSection['type']) => {
    const section = newSection(type)
    update(d => {
      const productsIndex = d.sections.findIndex(s => s.type === 'products')
      const sections = [...d.sections]
      sections.splice(productsIndex >= 0 ? productsIndex + 1 : sections.length, 0, section)
      return { ...d, sections }
    })
    setAdding(false); setEditing(section.id)
  }

  const saveLabel = { idle: 'Bez zmien', dirty: 'Neuložené…', saving: 'Ukladám…', saved: 'Koncept uložený', error: 'Chyba ukladania' }[save]

  return <div className="se">
    <div className="se-toolbar">
      <div className="se-toolbar-left">
        <button type="button" className="se-icon-btn" onClick={undo} disabled={!history.current.past.length} title="Späť (Ctrl+Z)">↶</button>
        <button type="button" className="se-icon-btn" onClick={redo} disabled={!history.current.future.length} title="Znova (Ctrl+Shift+Z)">↷</button>
        <span className={`se-save is-${save}`}>{saveLabel}</span>
        {unpublished ? <span className="se-badge is-warn">Nezverejnené zmeny</span> : <span className="se-badge is-ok">Zverejnené</span>}
      </div>
      <div className="se-devices" role="group" aria-label="Náhľad zariadenia">
        {([['desktop', '🖥', 'Počítač'], ['tablet', '▯', 'Tablet'], ['mobile', '📱', 'Mobil']] as const).map(([d, icon, label]) => <button key={d} type="button" className={device === d ? 'is-on' : ''} onClick={() => setDevice(d)} title={label}><span aria-hidden="true">{icon}</span> {label}</button>)}
      </div>
      <div className="se-toolbar-right">
        <a className="pa-ghost" href={SHOP_URL} target="_blank" rel="noopener">Otvoriť e-shop ↗</a>
        <button type="button" className="pa-ghost" onClick={() => void discard()} disabled={!unpublished}>Zahodiť zmeny</button>
        <button type="button" className="primary-btn" onClick={() => void publish()} disabled={publishing || !unpublished}>{publishing ? 'Zverejňujem…' : 'Zverejniť'}</button>
      </div>
    </div>
    {error && <p className="error-message se-msg" role="alert">{error}</p>}
    {notice && <p className="pa-message se-msg" role="status">{notice}</p>}

    <div className="se-body">
      <aside className="se-panel">
        <nav className="se-tabs" role="tablist">
          {([['sections', 'Sekcie'], ['style', 'Štýl'], ['header', 'Logo'], ['footer', 'Pätička'], ['company', 'Firma']] as Array<[Tab, string]>).map(([t, label]) =>
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? 'is-on' : ''} onClick={() => { setTab(t); setAdding(false) }}>{label}</button>)}
        </nav>

        <div className="se-panel-body">
          {tab === 'sections' && !editingSection && !adding && <>
            <p className="se-info">Poradie zmeníte potiahnutím. Na sekciu môžete kliknúť aj priamo v náhľade.</p>
            <ul className="se-sections">{design.sections.map(section => {
              const builtIn = ['hero', 'products', 'about', 'seasonal'].includes(section.type)
              return <li key={section.id} className={section.enabled ? '' : 'is-off'} {...sectionDrag.itemProps(section.id)}>
                <span className="drag-handle" aria-hidden="true">⋮⋮</span>
                <span className="se-sec-icon" aria-hidden="true">{ICONS[section.type]}</span>
                <button type="button" className="se-sec-name" onClick={() => setEditing(section.id)}>
                  <strong>{LABELS[section.type]}</strong>
                  <small>{'title' in section && section.title ? section.title : section.type === 'benefits' ? `${section.items.length} body` : section.type === 'quote' ? section.text.slice(0, 40) : ''}</small>
                </button>
                <button type="button" className="se-mini" title={section.enabled ? 'Skryť' : 'Zobraziť'} onClick={() => setSection({ ...section, enabled: !section.enabled } as SiteSection)}>{section.enabled ? '👁' : '◌'}</button>
                {!builtIn && <button type="button" className="se-mini" title="Duplikovať" onClick={() => update(d => { const i = d.sections.findIndex(s => s.id === section.id); const copy = { ...clone(section), id: newId(section.type) }; const sections = [...d.sections]; sections.splice(i + 1, 0, copy); return { ...d, sections } })}>⧉</button>}
                {!builtIn && <button type="button" className="se-mini is-danger" title="Odstrániť" onClick={() => { if (window.confirm(`Odstrániť blok „${LABELS[section.type]}“?`)) update(d => ({ ...d, sections: d.sections.filter(s => s.id !== section.id) })) }}>×</button>}
              </li>
            })}</ul>
            <button type="button" className="se-add" onClick={() => setAdding(true)}>＋ Pridať blok</button>
          </>}

          {tab === 'sections' && adding && <>
            <button type="button" className="se-back" onClick={() => setAdding(false)}>‹ Späť na sekcie</button>
            <h3 className="se-h">Pridať blok</h3>
            <div className="se-blocks">{BLOCKS.map(b => <button key={b.type} type="button" onClick={() => addBlock(b.type)}><span aria-hidden="true">{b.icon}</span><strong>{b.label}</strong><small>{b.hint}</small></button>)}</div>
          </>}

          {tab === 'sections' && editingSection && <>
            <button type="button" className="se-back" onClick={() => setEditing(null)}>‹ Späť na sekcie</button>
            <div className="se-h-row"><h3 className="se-h">{ICONS[editingSection.type]} {LABELS[editingSection.type]}</h3>
              <Toggle checked={editingSection.enabled} onChange={v => setSection({ ...editingSection, enabled: v } as SiteSection)} label={editingSection.enabled ? 'Zobrazená' : 'Skrytá'} /></div>
            <SectionEditor section={editingSection} onChange={setSection} />
          </>}

          {tab === 'style' && <>
            <h3 className="se-h">Farebné schémy</h3>
            <div className="se-palettes">{PALETTES.map(p => {
              const on = (['primary', 'background', 'surface', 'text', 'soft'] as const).every(k => theme[k] === p.theme[k])
              return <button key={p.name} type="button" className={on ? 'is-on' : ''} onClick={() => setTheme(p.theme)}>
                <span className="se-swatches">{[p.theme.primary, p.theme.soft, p.theme.background, p.theme.text].map((c, i) => <i key={i} style={{ background: c }} />)}</span>{p.name}
              </button>
            })}</div>
            <h3 className="se-h">Vlastné farby</h3>
            <ColorField label="Hlavná farba (tlačidlá, akcenty)" value={theme.primary} onChange={v => setTheme({ primary: v })} />
            <ColorField label="Pozadie stránky" value={theme.background} onChange={v => setTheme({ background: v })} />
            <ColorField label="Karty a panely" value={theme.surface} onChange={v => setTheme({ surface: v })} />
            <ColorField label="Jemné pozadie sekcií" value={theme.soft} onChange={v => setTheme({ soft: v })} />
            <ColorField label="Text" value={theme.text} onChange={v => setTheme({ text: v })} />
            <h3 className="se-h">Písmo</h3>
            <Field label="Nadpisy"><select value={theme.headingFont} onChange={e => setTheme({ headingFont: e.target.value })} style={{ fontFamily: theme.headingFont }}>{HEADING_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}</select></Field>
            <Field label="Text"><select value={theme.bodyFont} onChange={e => setTheme({ bodyFont: e.target.value })}>{BODY_FONTS.map(f => <option key={f} value={f}>{f}</option>)}</select></Field>
            <Field label="Farebný riadok nadpisu"><Segmented value={theme.headingStyle} options={[['normal', 'Rovné'], ['italic', 'Kurzíva']]} onChange={v => setTheme({ headingStyle: v })} /></Field>
            <h3 className="se-h">Tvary</h3>
            <Field label="Zaoblenie rohov"><Segmented value={theme.radius} options={[['sharp', '▢ Ostré'], ['soft', '▢ Jemné'], ['round', '◯ Oblé']]} onChange={v => setTheme({ radius: v })} /></Field>
            {defaults && <button type="button" className="se-mini-link se-reset" onClick={() => { if (window.confirm('Vrátiť farby a písmo na pôvodný vzhľad Emili?')) setTheme(defaults.theme) }}>Obnoviť pôvodný štýl</button>}
          </>}

          {tab === 'header' && <>
            <ImageField label="Logo (obrázok)" value={brand.logoUrl} onChange={v => setBrand({ logoUrl: v })} hint="Najlepšie PNG s priehľadným pozadím. Bez loga sa zobrazí písmeno/emotikon a názov." />
            {brand.logoUrl && <Field label={`Výška loga: ${brand.logoHeight} px`}><input type="range" min={24} max={96} value={brand.logoHeight} onChange={e => setBrand({ logoHeight: Number(e.target.value) })} /></Field>}
            {!brand.logoUrl && <EmojiField value={brand.mark} onChange={v => setBrand({ mark: v.slice(0, 4) })} label="Znak v krúžku (písmeno alebo emotikon)" />}
            <Field label="Názov" hint={brand.logoUrl ? 'Ak logo obsahuje názov, nechajte prázdne.' : undefined}><TextInput value={brand.name} max={60} onChange={v => setBrand({ name: v })} /></Field>
            <Field label="Podnadpis"><TextInput value={brand.tagline} max={60} onChange={v => setBrand({ tagline: v })} /></Field>
            <h3 className="se-h">Favicon</h3>
            <ImageField label="Ikona stránky (favicon)" value={brand.faviconUrl} onChange={v => setBrand({ faviconUrl: v })} hint="Malá ikonka v záložke prehliadača. Najlepšie štvorcový obrázok (napr. 64×64 px), PNG. Bez obrázka sa použije predvolená ikona prehliadača." />
          </>}

          {tab === 'footer' && <>
            <Field label="Krátky text v pätičke"><TextInput value={foot.text} max={300} multiline onChange={v => setFoot({ text: v })} placeholder="Napr. Tešíme sa na vás!" /></Field>
            <Field label="Adresa"><TextInput value={foot.address} max={160} onChange={v => setFoot({ address: v })} /></Field>
            <Field label="Telefón" hint="Prázdne = telefón firmy"><TextInput value={foot.phone} max={80} onChange={v => setFoot({ phone: v })} /></Field>
            <Field label="E-mail" hint="Prázdne = e-mail firmy"><TextInput value={foot.email} max={120} onChange={v => setFoot({ email: v })} /></Field>
            <Field label="Facebook (https://…)"><input value={foot.facebookUrl} maxLength={500} onChange={e => setFoot({ facebookUrl: e.target.value.trim() })} placeholder="https://www.facebook.com/…" /></Field>
            <Field label="Instagram (https://…)"><input value={foot.instagramUrl} maxLength={500} onChange={e => setFoot({ instagramUrl: e.target.value.trim() })} placeholder="https://www.instagram.com/…" /></Field>
            <Toggle checked={foot.showSeller} onChange={v => setFoot({ showSeller: v })} label="Zobraziť údaje firmy (IČO, sídlo) – povinné pre e-shop" />
            <button type="button" className="pa-link" onClick={() => setTab('company')}>Upraviť údaje firmy →</button>
          </>}

          {tab === 'company' && <SellerPanel onPreview={seller => postToFrame({ type: 'emili-seller-preview', seller: { ...seller, registeredOffice: [seller.street, seller.city].filter(Boolean).join(', ') } })} />}
        </div>
      </aside>

      <div className={`se-stage is-${device}`}>
        <div className="se-frame-wrap">
          {!frameReady && <div className="se-frame-loading">Načítavam náhľad e-shopu…<small>Ak sa nenačíta, skontrolujte, či beží e-shop ({SHOP_URL}).</small></div>}
          <iframe ref={frame} title="Náhľad e-shopu" src={`${SHOP_URL}/?nahlad=1`} />
        </div>
      </div>
    </div>
  </div>
}

/** Údaje firmy – pätička, obchodné podmienky, faktúry a e-maily. Ukladajú sa hneď (nie cez Zverejniť). */
type Seller = { companyName: string; street: string; city: string; country: string; ico: string; dic: string; icDph: string; vatPayer: boolean; registerEntry: string; email: string; phone: string; web: string; bankName: string; iban: string }

function SellerPanel({ onPreview }: { onPreview: (seller: Seller) => void }) {
  const [saved, setSaved] = useState<Seller | null>(null)
  const [draft, setDraft] = useState<Seller | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  useEffect(() => {
    adminRequest('/api/admin/seller').then(data => { setSaved(data.seller); setDraft(data.seller) }).catch(e => setError(e instanceof Error ? e.message : 'Údaje firmy sa nepodarilo načítať.'))
  }, [])
  useEffect(() => { if (draft) onPreview(draft) }, [draft]) // eslint-disable-line react-hooks/exhaustive-deps
  if (!draft || !saved) return <p className="se-hint">{error || 'Načítavam údaje firmy…'}</p>
  const set = (patch: Partial<Seller>) => { setDraft({ ...draft, ...patch }); setNotice('') }
  const changed = JSON.stringify(draft) !== JSON.stringify(saved)
  async function save() {
    setBusy(true); setError(''); setNotice('')
    try {
      const data = await adminRequest('/api/admin/seller', { method: 'PUT', body: JSON.stringify(draft) })
      setSaved(data.seller); setDraft(data.seller); setNotice('Údaje firmy sú uložené a platia hneď.')
    } catch (e) { setError(e instanceof Error ? e.message : 'Uloženie sa nepodarilo.') } finally { setBusy(false) }
  }
  return <div className="se-seller">
    <p className="se-hint se-seller-intro">Zobrazujú sa v pätičke, v obchodných podmienkach, v e-mailoch a na nových faktúrach. Už vystavené faktúry sa nezmenia. Zmeny sa ukladajú samostatne tlačidlom dole – nie cez Zverejniť.</p>
    <h4>Firma</h4>
    <Field label="Obchodné meno *" hint="Presne podľa obchodného registra"><TextInput value={draft.companyName} max={150} onChange={v => set({ companyName: v })} /></Field>
    <Field label="Sídlo – ulica a číslo *"><TextInput value={draft.street} max={150} onChange={v => set({ street: v })} /></Field>
    <Field label="Sídlo – PSČ a mesto *"><TextInput value={draft.city} max={100} onChange={v => set({ city: v })} placeholder="972 17 Kanianka" /></Field>
    <Field label="Štát"><TextInput value={draft.country} max={80} onChange={v => set({ country: v })} /></Field>
    <div className="se-row">
      <Field label="IČO *"><TextInput value={draft.ico} max={12} onChange={v => set({ ico: v.replace(/\s/g, '') })} /></Field>
      <Field label="DIČ"><TextInput value={draft.dic} max={12} onChange={v => set({ dic: v.replace(/\s/g, '') })} /></Field>
    </div>
    <Toggle checked={draft.vatPayer} onChange={v => set({ vatPayer: v })} label="Platiteľ DPH" />
    {draft.vatPayer && <Field label="IČ DPH *"><TextInput value={draft.icDph} max={14} onChange={v => set({ icDph: v.replace(/\s/g, '').toUpperCase() })} placeholder="SK2120041154" /></Field>}
    <Field label="Zápis v registri" hint="Napr. Obchodný register Okresného súdu Trenčín, oddiel Sro, vložka č. …"><TextInput value={draft.registerEntry} max={250} multiline rows={2} onChange={v => set({ registerEntry: v })} /></Field>
    <h4>Kontakt</h4>
    <Field label="E-mail *"><TextInput value={draft.email} max={150} onChange={v => set({ email: v.trim() })} /></Field>
    <Field label="Telefón *" hint="Viac čísel oddeľte čiarkou"><TextInput value={draft.phone} max={120} onChange={v => set({ phone: v })} /></Field>
    <Field label="Web"><TextInput value={draft.web} max={120} onChange={v => set({ web: v.trim() })} placeholder="www.emili.sk" /></Field>
    <h4>Bankové spojenie <small>– iba na faktúrach</small></h4>
    <Field label="Banka"><TextInput value={draft.bankName} max={120} onChange={v => set({ bankName: v })} /></Field>
    <Field label="IBAN"><TextInput value={draft.iban} max={42} onChange={v => set({ iban: v.toUpperCase() })} /></Field>
    {error && <p className="error-message" role="alert">{error}</p>}
    {notice && <p className="pa-message" role="status">{notice}</p>}
    <div className="se-seller-actions">
      {changed && <button type="button" className="pa-ghost" disabled={busy} onClick={() => setDraft(saved)}>Zrušiť</button>}
      <button type="button" className="primary-btn" disabled={!changed || busy} onClick={() => void save()}>{busy ? 'Ukladám…' : changed ? 'Uložiť údaje firmy' : 'Uložené'}</button>
    </div>
  </div>
}
